'use client';
import { useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { Input } from '@repo/videa-components/src/ui/input';
import { Label } from '@repo/videa-components/src/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { Skeleton } from '@repo/videa-components/src/ui/skeleton';
import { Textarea } from '@repo/videa-components/src/ui/textarea';

import { useGetGptResponse, validModels } from './useGetGptResponse';

export default function NarrativeDevTool({
  defaultSystemPrompt,
  defaultUserInput,
  defaultModel,
  defaultTemperature,
  defaultTopP,
  defaultFrequencyPenalty,
  defaultPresencePenalty,
  narrativeResponse,
}: {
  defaultSystemPrompt: string;
  defaultUserInput: string;
  defaultModel: string;
  defaultTemperature: number;
  defaultTopP: number;
  defaultFrequencyPenalty: number;
  defaultPresencePenalty: number;
  narrativeResponse: string;
}) {
  const [config, setConfig] = useState({
    systemPrompt: defaultSystemPrompt,
    userInput: defaultUserInput,
    model: defaultModel,
    temperature: defaultTemperature,
    top_p: defaultTopP,
    frequency_penalty: defaultFrequencyPenalty,
    presence_penalty: defaultPresencePenalty,
  });

  const [apiOutput, setApiOutput] = useState(narrativeResponse);

  const getUpdatedConfigValue = (name: string, value: string) => {
    if (name === 'systemPrompt' || name === 'userInput' || name === 'model') {
      return value;
    }
    if (value === '') {
      return '';
    }
    return Number(value);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: getUpdatedConfigValue(name, value),
    }));
  };

  const { mutate, isLoading, error } = useGetGptResponse({
    onSuccess: (data) => {
      setApiOutput(data.content ?? '');
    },
  });

  return (
    <div className="vai-overflow-y-auto">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutate({
            prompt: config.systemPrompt,
            input: config.userInput,
            model: config.model as (typeof validModels)[number],
            temperature: config.temperature,
            top_p: config.top_p,
            frequency_penalty: config.frequency_penalty,
            presence_penalty: config.presence_penalty,
          });
        }}
        className="vai-space-y-4"
        style={{ zIndex: 1400 }}
      >
        <div className="vai-space-y-2">
          <Label htmlFor="systemPrompt">System Prompt</Label>
          <Textarea
            id="systemPrompt"
            name="systemPrompt"
            value={config.systemPrompt}
            onChange={handleInputChange}
            placeholder="Enter system prompt"
            autoResize
          />
        </div>
        <div className="vai-space-y-2">
          <Label htmlFor="userInput">User Input</Label>
          <Textarea
            id="userInput"
            name="userInput"
            value={config.userInput}
            onChange={handleInputChange}
            placeholder="Enter user input"
            autoResize
          />
        </div>
        <div className="vai-space-y-2">
          <Label htmlFor="model">Model</Label>
          <Select
            name="model"
            value={config.model}
            onValueChange={(value: string) =>
              handleInputChange({
                target: { name: 'model', value },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {validModels.map((model) => (
                <SelectItem key={model} value={model}>
                  {model}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="vai-grid vai-grid-cols-2 vai-gap-4">
          <div className="vai-space-y-2">
            <Label htmlFor="temperature">Temperature</Label>
            <p className="vai-flex vai-items-start vai-mt-2 vai-text-xs vai-text-slate-400">
              <Trans>
                What sampling temperature to use, between 0 and 2. Higher values
                like 0.8 will make the output more random, while lower values
                like 0.2 will make it more focused and deterministic. We
                generally recommend altering this or top_p but not both.
              </Trans>
            </p>
            <Input
              id="temperature"
              name="temperature"
              type="number"
              value={config.temperature}
              onChange={handleInputChange}
              placeholder="0"
              className="vai-w-4/6"
            />
          </div>
          <div className="vai-space-y-2">
            <Label htmlFor="top_p">Top P</Label>
            <p className="vai-flex vai-items-start vai-mt-2 vai-text-xs vai-text-slate-400">
              <Trans>
                An alternative to sampling with temperature, called nucleus
                sampling, where the model considers the results of the tokens
                with top_p probability mass. So 0.1 means only the tokens
                comprising the top 10% probability mass are considered. We
                generally recommend altering this or temperature but not both.
              </Trans>
            </p>
            <Input
              id="top_p"
              name="top_p"
              type="number"
              value={config.top_p}
              onChange={handleInputChange}
              placeholder="0"
              className="vai-w-4/6"
            />
          </div>
          <div className="vai-space-y-2">
            <Label htmlFor="frequency_penalty">Frequency Penalty</Label>
            <p className="vai-flex vai-items-start vai-mt-2 vai-text-xs vai-text-slate-400">
              <Trans>
                Number between -2.0 and 2.0. Positive values penalize new tokens
                based on their existing frequency in the text so far, decreasing
                the model&apos;s likelihood to repeat the same line verbatim.
              </Trans>
            </p>
            <Input
              id="frequency_penalty"
              name="frequency_penalty"
              type="number"
              value={config.frequency_penalty}
              onChange={handleInputChange}
              placeholder="0"
              className="vai-w-4/6"
            />
          </div>
          <div className="vai-space-y-2">
            <Label htmlFor="presence_penalty">Presence Penalty</Label>
            <p className="vai-flex vai-items-start vai-mt-2 vai-text-xs vai-text-slate-400">
              <Trans>
                Number between -2.0 and 2.0. Positive values penalize new tokens
                based on whether they appear in the text so far, increasing the
                model&apos;s likelihood to talk about new topics.
              </Trans>
            </p>
            <Input
              id="presence_penalty"
              name="presence_penalty"
              type="number"
              value={config.presence_penalty}
              onChange={handleInputChange}
              placeholder="0"
              className="vai-w-4/6"
            />
          </div>
        </div>
        <Button type="submit" className="vai-w-full" disabled={isLoading}>
          Submit
        </Button>
        {error && (
          <p className="vai-text-red-500">
            <Trans>Error: {error.response?.data?.message}</Trans>
          </p>
        )}
      </form>

      <div className="vai-mt-4 vai-space-y-2">
        <Label htmlFor="apiOutput">API Output</Label>
        {isLoading && <Skeleton className="vai-h-[100px]" />}
        {apiOutput && !isLoading && (
          <Textarea
            id="apiOutput"
            value={apiOutput}
            readOnly
            className="vai-h-[100px]"
            autoResize
          />
        )}
      </div>
    </div>
  );
}
